import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Deal {
  id: string;
  discount_percentage: number;
  badge_text: string;
  end_date: string;
  featured?: boolean;
  deal_type?: 'percentage' | 'fixed_amount' | 'bundle' | 'quantity';
  min_quantity?: number;
  is_flash_deal?: boolean;
  priority?: number;
  category_filter?: string | null;
  product_id?: string | null;
}

export const useProductDeal = (productId: string, productCategory?: string) => {
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchDeal = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date().toISOString();
      
      // Recherche manuelle avec query builder pour éviter les problèmes de types
      // 1. D'abord chercher un deal spécifique au produit
      const { data: allDeals, error } = await supabase
        .from('deals')
        .select('*')
        .eq('active', true)
        .lte('start_date', now)
        .gte('end_date', now);

      if (error) {
        console.error('Error fetching deals:', error);
        setDeal(null);
        return;
      }

      // Filtrer côté client pour éviter les problèmes de types TypeScript
      const productDeals = (allDeals as any[])?.filter((d: any) => d.product_id === productId) || [];
      const categoryDeals = productCategory 
        ? (allDeals as any[])?.filter((d: any) => d.category_filter === productCategory && !d.product_id) || []
        : [];

      // Combiner et trier par priorité puis discount
      const allMatchingDeals = [...productDeals, ...categoryDeals].sort((a: any, b: any) => {
        // Produits ont priorité sur catégories
        if (a.product_id && !b.product_id) return -1;
        if (!a.product_id && b.product_id) return 1;
        // Puis par priorité
        if ((b.priority || 0) !== (a.priority || 0)) return (b.priority || 0) - (a.priority || 0);
        // Puis par pourcentage
        return (b.discount_percentage || 0) - (a.discount_percentage || 0);
      });

      if (allMatchingDeals.length > 0) {
        setDeal(allMatchingDeals[0] as Deal);
      } else {
        setDeal(null);
      }
    } catch (error) {
      console.error('Error fetching deal:', error);
      setDeal(null);
    } finally {
      setLoading(false);
    }
  }, [productId, productCategory]);

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    fetchDeal();

    const channel = supabase
      .channel(`product-deal-${productId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'deals',
          filter: `product_id=eq.${productId}`
        },
        () => fetchDeal()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [productId, productCategory, fetchDeal]);

  const calculateDiscountedPrice = useCallback((originalPrice: number, quantity: number = 1) => {
    if (!deal) return originalPrice;
    
    // Vérifier la quantité minimale pour les deals quantity-based
    if (deal.deal_type === 'quantity' && quantity < (deal.min_quantity || 1)) {
      return originalPrice;
    }
    
    // Calculer selon le type de deal
    switch (deal.deal_type) {
      case 'fixed_amount':
        // Réduction fixe en centimes
        return Math.max(0, originalPrice - deal.discount_percentage);
      
      case 'percentage':
      default:
        // Réduction en pourcentage (comportement par défaut)
        return originalPrice * (1 - deal.discount_percentage / 100);
    }
  }, [deal]);

  return { deal, loading, calculateDiscountedPrice };
};
